/* eslint-disable react/button-has-type */
import { SVGProps } from 'react'
import styled, { css } from 'styled-components'
import { buttonSizes } from './buttonSizes'
// import ButtonIcon from './ButtonIcon'
import { ButtonBaseProps, buttonTypes } from './buttonTypes'
import { LoadingOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { theme } from '../../config/theme'
import Text from '../Typography/Text/Text'
import Icon from '../Icon'
import IconType from '../Icon/iconTypes'

export type ButtonVariant =
  | 'primary'
  | 'default'
  | 'text'
  | 'danger'
  | 'popout'
  | 'textDanger'

export type CustomButtonProps = {
  /**
   * @default 'primary'
   */
  variant?: ButtonVariant
  /**
   * @deprecated
   * use children for icon
   */
  icon?: React.ReactElement<SVGProps<SVGElement>> | null
  /**
   * @default 'left'
   * @deprecated
   * use children for icon
   */
  iconAlign?: 'left' | 'right'
  /**
   * @default 'normal'
   */
  size?: 'normal' | 'large'
  /**
   * @default false
   */
  isLoading?: boolean
  ariaLabel?: string
}

export type ButtonProps<T extends React.ElementType = 'button'> = {
  as?: T
  children?: React.ReactNode
  disabledByPermission?: boolean
} & React.ComponentPropsWithoutRef<T> &
  CustomButtonProps

type ButtonStyledProps = {
  $variant: ButtonVariant
  $iconAlign: 'left' | 'right'
  $size: 'normal' | 'large'
  $hasIcon: boolean
} & ButtonBaseProps

const Space = styled.div`
  margin-left: 8px;
`

const ButtonStyled = styled.button<ButtonStyledProps>`
  all: unset;
  cursor: pointer;
  border-radius: 9px;
  border: 1px solid transparent;

  // text proos
  font-size: 16px;
  line-height: 24px;
  font-weight: ${({ theme }) => theme?.font?.weight?.medium};
  text-decoration: none;

  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
  }

  ${({ $size }) => buttonSizes[$size]}
  ${({ $variant }) => buttonTypes[$variant]}

  // here we add only global disabled props (for all button types)
  // modify buttonTypes file for specific types
  &:disabled {
    cursor: not-allowed;
  }
  // icon props
  display: inline-flex;
  align-items: center;
  height: fit-content;

  a {
    color: inherit;
  }
`

const Button = <T extends React.ElementType = 'button'>(
  props: ButtonProps<T>
) => {
  const {
    children,
    variant = 'default',
    isLoading = false,
    icon,
    iconAlign = 'left',
    size = 'normal',
    onClick,
    ariaLabel,
    ...restProps
  } = props

  const onClickHandler = (event: React.MouseEvent) => {
    if (isLoading) {
      event.preventDefault()

      return
    }

    onClick?.(event)
  }
  const shouldRenderIcon = Boolean(icon || isLoading)
  const shouldRenderIconSpace = shouldRenderIcon && children

  const buttonComponent = (
    <ButtonStyled
      $size={size}
      $hasIcon={!!icon}
      $iconAlign={iconAlign}
      $variant={variant}
      $isLoading={isLoading}
      onClick={onClickHandler}
      type="button"
      aria-label={ariaLabel}
      {...restProps}
      disabled={restProps.disabled || restProps.disabledByPermission}
    >
      <>
        <IconWrapper $shouldRender={shouldRenderIcon}>
          {isLoading && <LoadingOutlined data-testid="spinner" />}
          {icon && !isLoading && (
            <IconContainer $iconAlign={iconAlign}>
              {icon}
              {/* this space is the main reason why we don't render the Icon as a children */}
            </IconContainer>
          )}
        </IconWrapper>
        {shouldRenderIconSpace && <Space />}
        <ChildrenWrapper>{children}</ChildrenWrapper>
      </>
    </ButtonStyled>
  )

  if (restProps?.disabledByPermission) {
    return (
      <Tooltip
        placement="bottom"
        color={theme.color.neutral.color3}
        title={
          <TooltipTextWrapper>
            <Icon customIcon={IconType.CircleXIcon} />
            <StyledText level={1}>Permissions required</StyledText>
          </TooltipTextWrapper>
        }
      >
        {buttonComponent}
      </Tooltip>
    )
  }

  return buttonComponent
}

const ChildrenWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TooltipTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div<{ $shouldRender: boolean }>`
  transition: all 0.3s ease-in-out;

  width: 0px;
  opacity: 0;
  margin-right: 0px;

  ${({ $shouldRender: isLoading }) =>
    isLoading &&
    css`
      width: 16px;
      opacity: 1;
    `}
`

const IconContainer = styled.div<{ $iconAlign: 'right' | 'left' }>`
  display: flex;

  ${({ $iconAlign: iconAlign }) =>
    iconAlign === 'right' &&
    css`
      order: 3;
      flex-direction: row-reverse;
    `};
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`

export default Button
